import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from '../../utils/axiosConfig';

import { fetchEvents, deleteEvent } from '../../store/slices/eventSlice';
import { useParams, Link } from 'react-router-dom';
import {
  Plus,
  Calendar,
  MapPin,
  DollarSign,
  Users,
  Edit3,
  Trash2,
  Ticket,
  ArrowRight,
  ChevronRight,
  ShoppingCart,
  BarChart3,
  TrendingUp,
  Camera,
  Activity,
  Wallet,
  Loader2,
  CheckCircle
} from 'lucide-react';

import GuestList from './GuestList';
import ManualTicketModal from './ManualTicketModal';
import { X } from 'lucide-react';
import { isEventPast, isEventUpcoming } from "../../utils/date";

const getEventDates = (event) => {
  const rawDates = Array.isArray(event?.dates) && event.dates.length > 0 ? event.dates : [event?.date];
  return [...new Set(rawDates.filter(Boolean).map((value) => new Date(value).toISOString()))].sort();
};

const getEventLastDate = (event) => {
  const dates = getEventDates(event);
  return dates[dates.length - 1] || event.date;
};

const formatEventDates = (event) => getEventDates(event)
  .map((value) => new Date(value).toLocaleDateString('es-AR', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' }))
  .join(' y ');

const AdminEvents = () => {
  const dispatch = useDispatch();
  const { events, loading, error } = useSelector((state) => state.events);
  const { slug } = useParams();
  const [selectedEventForGuestList, setSelectedEventForGuestList] = React.useState(null);
  const [selectedEventForManualIssue, setSelectedEventForManualIssue] = React.useState(null);
  const [selectedEventForBookingPayment, setSelectedEventForBookingPayment] = React.useState(null);
  const [cliente, setCliente] = React.useState(null);

  useEffect(() => {
    const fetchCliente = async () => {
      try {
        const response = await axios.get(`/api/clients/${slug}`);
        setCliente(response.data);
      } catch (err) {
        console.error('Error fetching client for SAAS check:', err);
      }
    };
    fetchCliente();
  }, [slug]);


  const refreshEvents = React.useCallback(() => {
    if (slug) dispatch(fetchEvents(slug));
  }, [dispatch, slug]);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshEvents();
      }
    };

    const interval = window.setInterval(refreshWhenVisible, 15000);
    window.addEventListener('focus', refreshEvents);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshEvents);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshEvents]);

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de eliminar este evento?')) {
      dispatch(deleteEvent({ slug, id }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-6 rounded-[32px] max-w-md text-center">
          <p className="text-lg font-black mb-2">Error al cargar eventos</p>
          <p className="text-sm font-medium opacity-60">
            {typeof error === 'object' ? error.message || JSON.stringify(error) : error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30 pb-20">
      {/* HERO / HEADER */}
      <div className="relative pt-12 pb-24 px-6 border-b border-white/5 bg-white/[0.01]">
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-orange/5 rounded-full blur-[120px] animate-glow" />
        </div>

        <div className="mx-auto max-w-7xl relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full mb-4">
              <Calendar size={12} className="text-primary-orange" />
              <span className="text-[10px] font-black uppercase tracking-[2px] text-white/60">Organizador</span>
              <span className="text-[10px] font-black uppercase text-white/40 tracking-widest text-center px-2">Transmisiones</span>
            </div>
            <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-2 uppercase">Gestión de <span className="text-gradient leading-relaxed">Eventos</span></h1>
            <p className="text-white/40 font-medium tracking-tight">Administrá tus fechas, controlá la capacidad y editá la info de tus shows.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Link
              to={`/${slug}/admin/agenda`}
              className="group px-6 py-5 rounded-[24px] font-black text-white/80 border border-white/10 bg-white/5 hover:bg-white/10 transition-all flex items-center justify-center gap-3"
            >
              <Calendar size={22} />
              VER AGENDA
            </Link>
            <Link
              to={`/${slug}/admin/events/create`}
              className="group bg-brand-gradient px-8 py-5 rounded-[24px] font-black text-white shadow-xl shadow-orange-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
            >
              <Plus size={24} />
              CREAR EVENTO
              <ChevronRight className="opacity-40 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* GRID */}
      <div className="mx-auto max-w-7xl px-6 -mt-12 relative z-10">
        {events.length === 0 ? (
          <div className="bg-glass p-20 rounded-[48px] text-center border-dashed flex flex-col items-center">
            <div className="w-16 h-16 bg-white/5 rounded-3xl flex items-center justify-center text-white/10 mb-6 font-black italic text-3xl">?</div>
            <p className="text-white/20 font-black uppercase tracking-widest text-sm mb-8">Aún no creaste ningún evento</p>
            <Link
              to={`/${slug}/admin/events/create`}
              className="text-primary-orange font-black flex items-center gap-2 hover:gap-4 transition-all"
            >
              EMPEZÁ AHORA <ArrowRight size={20} />
            </Link>
          </div>
        ) : (
          <div className="space-y-20">
            {/* Próximos Eventos */}
            {events.filter(ev => isEventUpcoming(getEventLastDate(ev))).length > 0 && (
              <section>
                <div className="flex items-center gap-4 mb-10">
                  <h2 className="text-xl font-black text-white tracking-tight uppercase italic">Próximos <span className="text-primary-orange">Eventos</span></h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {events
                    .filter(ev => isEventUpcoming(getEventLastDate(ev)))
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((event) => (
                      <AdminEventCard
                        key={event._id}
                        event={event}
                        slug={slug}
                        handleDelete={handleDelete}
                        onOpenGuestList={setSelectedEventForGuestList}
                        onOpenManualIssue={setSelectedEventForManualIssue}
                        onOpenBookingPayment={setSelectedEventForBookingPayment}
                        billingModel={cliente?.billingModel}
                        subscriptionStatus={cliente?.subscriptionStatus}
                      />
                    ))}
                </div>
              </section>
            )}

            {/* Eventos Pasados */}
            {events.filter(ev => isEventPast(getEventLastDate(ev))).length > 0 && (
              <section className="opacity-70">
                <div className="flex items-center gap-4 mb-10">
                  <h2 className="text-lg font-bold text-white/30 tracking-widest uppercase">Eventos Finalizados</h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-white/5 to-transparent"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {events
                    .filter(ev => isEventPast(getEventLastDate(ev)))
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((event) => (
                      <AdminEventCard
                        key={event._id}
                        event={event}
                        slug={slug}
                        handleDelete={handleDelete}
                        isPast={true}
                        onOpenGuestList={setSelectedEventForGuestList}
                        onOpenManualIssue={setSelectedEventForManualIssue}
                        onOpenBookingPayment={setSelectedEventForBookingPayment}
                        billingModel={cliente?.billingModel}
                        subscriptionStatus={cliente?.subscriptionStatus}
                      />
                    ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Guest List Modal */}
      {selectedEventForGuestList && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-200">
          <div className="bg-[#080808] border border-white/10 rounded-[32px] w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-2xl animate-in zoom-in-95 duration-200">
            <button
              onClick={() => setSelectedEventForGuestList(null)}
              className="absolute top-6 right-6 p-2 bg-white/5 rounded-full hover:bg-white/10 transition-all z-10"
            >
              <X size={20} />
            </button>

            <div className="p-8">
              <div className="mb-8">
                <p className="text-[10px] font-black uppercase tracking-widest text-primary-orange mb-2">Guest List</p>
                <h2 className="text-3xl font-black text-white">{selectedEventForGuestList.title}</h2>
                <div className="flex items-center gap-2 mt-2 text-white/40 text-sm font-medium">
                  <span>{new Date(selectedEventForGuestList.date).toLocaleDateString('es-AR', { timeZone: 'UTC' })}</span>
                  <span>•</span>
                  <span>
                    {selectedEventForGuestList.salesMode === 'booking'
                      ? 'cupo manual'
                      : `${selectedEventForGuestList.totalTickets} capacidad total`}
                  </span>
                </div>
              </div>

              <GuestList
                eventId={selectedEventForGuestList._id}
                eventTitle={selectedEventForGuestList.title}
                totalTickets={selectedEventForGuestList.totalTickets}
                soldTickets={selectedEventForGuestList.soldTickets}
                freeTicketsPercentage={selectedEventForGuestList.freeTicketsPercentage}
                unlimitedInvitations={selectedEventForGuestList.salesMode === 'booking'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Manual Issue Modal */}
      {selectedEventForManualIssue && (
         <ManualTicketModal
             event={selectedEventForManualIssue}
             onClose={() => setSelectedEventForManualIssue(null)}
         />
      )}

      {selectedEventForBookingPayment && (
        <BookingPaymentModal
          event={selectedEventForBookingPayment}
          slug={slug}
          onClose={() => setSelectedEventForBookingPayment(null)}
          onSaved={() => {
            setSelectedEventForBookingPayment(null);
            refreshEvents();
          }}
        />
      )}

    </div>
  );
};

const AdminEventCard = ({ event, slug, handleDelete, isPast = false, onOpenGuestList, onOpenManualIssue, onOpenBookingPayment, billingModel, subscriptionStatus }) => {
  const isBooking = event.salesMode === 'booking';
  const soldPercentage = Math.min(100, Math.round((event.soldTickets || 0) / (event.totalTickets || 1) * 100));
  const bookingAmount = Number(event.bookingAmount) || 0;
  const bookingPaidAmount = Number(event.bookingPaidAmount) || 0;
  const bookingBalance = Math.max(0, bookingAmount - bookingPaidAmount);
  const bookingStatuses = {
    paid: { label: 'Cobrado completo', className: 'text-green-400 bg-green-500/10 border-green-500/20' },
    partial: { label: 'Seña parcial', className: 'text-yellow-300 bg-yellow-500/10 border-yellow-500/20' },
    pending: { label: 'Pendiente', className: 'text-red-300 bg-red-500/10 border-red-500/20' },
  };
  const bookingStatus = bookingStatuses[event.bookingPaymentStatus] || bookingStatuses.pending;

  return (
    <div
      className={`bg-glass rounded-[40px] shadow-3xl overflow-hidden group hover:border-white/20 transition-all duration-500 shine-effect flex flex-col ${isPast ? 'grayscale-[0.5]' : ''}`}
    >
      {/* Portada */}
      <div className="h-48 relative overflow-hidden bg-white/5 border-b border-white/5">
        {event.coverImage || event.coverUrl ? (
          <img
            src={event.coverImage || event.coverUrl}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center opacity-10">
            <Ticket size={64} />
          </div>
        )}
        {isPast && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[2px]">
            <div className="bg-white/10 border border-white/20 px-4 py-2 rounded-xl backdrop-blur-md">
              <span className="text-[10px] font-black uppercase tracking-[3px] text-white">Finalizado</span>
            </div>
          </div>
        )}
        {event.isPublic === false && (
          <div className="absolute top-4 left-4 px-3 py-1.5 bg-blue-500/80 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest border border-white/10 text-white flex items-center gap-1 shadow-lg shadow-blue-500/20">
            {isBooking ? 'Reserva' : 'Privado'}
          </div>
        )}
        <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest border border-white/10">
          ID: {event._id.slice(-6).toUpperCase()}
        </div>
      </div>

      <div className="p-8 flex-1 flex flex-col">
        <h3 className={`text-2xl font-black mb-6 group-hover:text-primary-orange transition-colors line-clamp-2 leading-tight ${isPast ? 'text-white/60' : ''}`}>
          {event.title}
        </h3>

        <div className="space-y-4 mb-8">
          <EventMeta icon={<Calendar size={14} />} label="Fechas" value={formatEventDates(event)} />
          <EventMeta icon={<DollarSign size={14} />} label={isBooking ? 'Monto acordado' : 'Precio'} value={`$${(isBooking ? bookingAmount : event.price || 0).toLocaleString('es-AR')}`} />
          <EventMeta icon={<MapPin size={14} />} label="Ubicación" value={event.city || event.location || 'No definida'} />
          {!isBooking && <EventMeta icon={<Users size={14} />} label="Capacidad" value={`${event.totalTickets} total`} />}
        </div>

        {/* Venta Progress */}
        {isBooking ? (
        <div className="mb-8 pt-4 border-t border-white/5 space-y-3">
          <div className={`inline-flex px-3 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${bookingStatus.className}`}>
            {bookingStatus.label}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/5 border border-white/5 p-3">
              <p className="text-[10px] font-black text-white/25 uppercase tracking-widest">Cobrado</p>
              <p className="text-lg font-black text-green-400">${bookingPaidAmount.toLocaleString('es-AR')}</p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/5 p-3">
              <p className="text-[10px] font-black text-white/25 uppercase tracking-widest">Saldo</p>
              <p className="text-lg font-black text-primary-orange">${bookingBalance.toLocaleString('es-AR')}</p>
            </div>
          </div>
        </div>
        ) : (
        <div className="mb-8 pt-4 border-t border-white/5">
          <div className="flex justify-between items-end mb-2">
            <p className="text-[10px] font-black text-white/30 uppercase tracking-widest flex items-center gap-1.5">
              <TrendingUp size={12} className="text-green-500" /> Venta
            </p>
            <p className="text-sm font-black text-white/80">{soldPercentage}% <span className="text-[10px] text-white/20 font-medium">({event.soldTickets || 0} vendidas)</span></p>
          </div>
          <div className="h-2 w-full bg-white/5 border border-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-gradient rounded-full shadow-[0_0_10px_rgba(255,140,0,0.3)] transition-all duration-1000"
              style={{ width: `${soldPercentage}%` }}
            />
          </div>
        </div>
        )}

        {/* Acciones */}
        <div className="mt-auto grid grid-cols-2 gap-3">
          {isBooking && (
            <button
              onClick={() => onOpenBookingPayment(event)}
              className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500/10 border border-emerald-500/25 rounded-2xl text-sm font-black text-emerald-300 hover:bg-emerald-500 hover:text-white transition-all shadow-[0_0_15px_-5px_rgba(16,185,129,0.35)]"
            >
              <Wallet size={16} /> {bookingBalance > 0 ? 'COBRAR SALDO' : 'COBRO COMPLETO'}
            </button>
          )}

          <Link
            to={`/${slug}/admin/events/${event._id}/pos`}
            className="flex items-center justify-center gap-2 px-2 py-3 bg-primary-orange/10 border border-primary-orange/20 rounded-2xl text-[11px] font-black text-primary-orange hover:bg-primary-orange hover:text-white transition-all shadow-[0_0_15px_-5px_rgba(255,107,0,0.3)]"
          >
            <ShoppingCart size={14} /> CAJA POS
          </Link>

          <Link
            to={`/${slug}/admin/events/${event._id}/bar`}
            className="flex items-center justify-center gap-2 px-2 py-3 bg-green-500/10 border border-green-500/20 rounded-2xl text-[11px] font-black text-green-400 hover:bg-green-500 hover:text-white transition-all shadow-[0_0_15px_-5px_rgba(74,222,128,0.2)]"
          >
            <BarChart3 size={14} /> STOCK & LIVE
          </Link>

          <Link
            to={`/${slug}/admin/events/${event._id}/photos`}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-500/10 border border-purple-500/20 rounded-2xl text-[11px] sm:text-sm font-black text-purple-400 hover:bg-purple-500 hover:text-white transition-all shadow-[0_0_15px_-5px_rgba(168,85,247,0.3)]"
          >
            <Camera size={16} /> TRANSMISIONES
          </Link>

          <a
            href={`/${slug}/events/${event._id}/live-access`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 bg-cyan-500/10 border border-cyan-500/20 rounded-2xl text-sm font-black text-cyan-400 hover:bg-cyan-500 hover:text-white transition-all shadow-[0_0_15px_-5px_rgba(6,182,212,0.3)]"
          >
            <Activity size={16} /> PANTALLA ACCESOS
          </a>

          <button
            onClick={() => onOpenGuestList(event)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-2xl text-sm font-black text-blue-400 hover:bg-blue-500 hover:text-white transition-all col-span-2"
          >
            <Users size={16} /> LISTA DE INVITADOS
          </button>
          
          {(isBooking || (billingModel === 'subscription' && subscriptionStatus === 'active')) && (
            <button
              onClick={() => onOpenManualIssue(event)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-[#009EE3]/10 border border-[#009EE3]/30 rounded-2xl text-[11px] sm:text-xs font-black text-[#009EE3] hover:bg-[#009EE3] hover:text-white transition-all col-span-2 shadow-[0_0_15px_-5px_rgba(0,158,227,0.3)]"
            >
              <Ticket size={16} /> EMITIR ENTRADA{isBooking ? '' : ' (SAAS)'}
            </button>
          )}

          <Link
            to={`/${slug}/admin/events/edit/${event._id}`}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-white/5 rounded-2xl text-sm font-black text-white hover:bg-white hover:text-black transition-all"
          >
            <Edit3 size={16} /> EDITAR
          </Link>
          <button
            onClick={() => handleDelete(event._id)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-2xl text-sm font-black text-red-400 hover:bg-red-500 hover:text-white transition-all"
          >
            <Trash2 size={16} /> BORRAR
          </button>
        </div>
      </div>
    </div >
  );
};

const BookingPaymentModal = ({ event, slug, onClose, onSaved }) => {
  const bookingAmount = Number(event.bookingAmount) || 0;
  const bookingPaidAmount = Number(event.bookingPaidAmount) || 0;
  const bookingBalance = Math.max(0, bookingAmount - bookingPaidAmount);
  const [paymentAmount, setPaymentAmount] = React.useState(bookingBalance > 0 ? String(bookingBalance) : '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [saved, setSaved] = React.useState(false);

  const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('es-AR')}`;
  const parsedPaymentAmount = Number(paymentAmount);
  const nextPaidAmount = Math.min(bookingAmount, bookingPaidAmount + (Number.isFinite(parsedPaymentAmount) ? parsedPaymentAmount : 0));
  const nextBalance = Math.max(0, bookingAmount - nextPaidAmount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!Number.isFinite(parsedPaymentAmount) || parsedPaymentAmount <= 0) {
      setError('Ingresá un importe mayor a cero.');
      return;
    }

    if (parsedPaymentAmount > bookingBalance) {
      setError('El importe no puede superar el saldo pendiente.');
      return;
    }

    const bookingPaymentStatus = nextPaidAmount >= bookingAmount ? 'paid' : nextPaidAmount > 0 ? 'partial' : 'pending';

    setLoading(true);
    try {
      await axios.put(`/${slug}/api/events/${event._id}`, {
        salesMode: 'booking',
        bookingAmount,
        bookingPaidAmount: nextPaidAmount,
        bookingPaymentStatus,
      });
      setSaved(true);
      window.setTimeout(onSaved, 500);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo registrar el cobro.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <div className="bg-[#080808] border border-white/10 rounded-[32px] w-full max-w-xl relative shadow-2xl animate-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 bg-white/5 rounded-full hover:bg-white/10 transition-all z-10"
        >
          <X size={20} />
        </button>

        <form onSubmit={handleSubmit} className="p-8">
          <div className="mb-8">
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-2">
              <Wallet size={13} /> Cobro de reserva
            </p>
            <h2 className="text-3xl font-black text-white pr-10">{event.title}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <div className="rounded-2xl bg-white/5 border border-white/5 p-4">
              <p className="text-[10px] font-black text-white/30 uppercase tracking-widest">Acordado</p>
              <p className="text-lg font-black text-white">{formatCurrency(bookingAmount)}</p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/5 p-4">
              <p className="text-[10px] font-black text-white/30 uppercase tracking-widest">Cobrado</p>
              <p className="text-lg font-black text-green-400">{formatCurrency(bookingPaidAmount)}</p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/5 p-4">
              <p className="text-[10px] font-black text-white/30 uppercase tracking-widest">Saldo</p>
              <p className="text-lg font-black text-primary-orange">{formatCurrency(bookingBalance)}</p>
            </div>
          </div>

          {saved ? (
            <div className="rounded-3xl border border-green-500/20 bg-green-500/10 p-8 text-center">
              <CheckCircle className="mx-auto mb-4 text-green-400" size={42} />
              <p className="text-xl font-black text-white">Cobro registrado</p>
              <p className="text-sm font-bold text-white/45 mt-2">El saldo del evento ya fue actualizado.</p>
            </div>
          ) : bookingBalance <= 0 ? (
            <div className="rounded-3xl border border-green-500/20 bg-green-500/10 p-8 text-center">
              <CheckCircle className="mx-auto mb-4 text-green-400" size={42} />
              <p className="text-xl font-black text-white">Este evento está cobrado completo</p>
              <p className="text-sm font-bold text-white/45 mt-2">No queda saldo pendiente para registrar.</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <label className="block text-xs font-black text-white/40 uppercase tracking-widest mb-2">Importe cobrado ahora</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="number"
                    min="1"
                    max={bookingBalance}
                    step="1"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-4 text-white text-xl font-black outline-none focus:border-emerald-400/60"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setPaymentAmount(String(bookingBalance))}
                    className="shrink-0 px-5 py-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-black uppercase hover:bg-emerald-500 hover:text-white transition-all"
                  >
                    Saldo total
                  </button>
                </div>
              </div>

              <div className="rounded-2xl bg-white/[0.03] border border-white/5 p-4 mb-6">
                <div className="flex items-center justify-between text-sm font-bold">
                  <span className="text-white/45">Quedaría cobrado</span>
                  <span className="text-green-400">{formatCurrency(nextPaidAmount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm font-bold mt-2">
                  <span className="text-white/45">Nuevo saldo</span>
                  <span className="text-primary-orange">{formatCurrency(nextBalance)}</span>
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/25 text-red-300 rounded-2xl p-4 text-sm font-bold mb-6">
                  {error}
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 rounded-xl font-bold text-white/60 hover:text-white hover:bg-white/5 transition-all text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 rounded-xl font-black text-white bg-emerald-500 hover:bg-emerald-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Wallet size={18} />}
                  Registrar cobro
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

const EventMeta = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="text-primary-orange/50">{icon}</div>
    <div className="flex flex-col">
      <p className="text-[10px] font-black text-white/20 uppercase tracking-[2px] leading-none mb-1">{label}</p>
      <p className="text-sm font-bold text-white/80 leading-none">{value}</p>
    </div>
  </div>
);

export default AdminEvents;
